import { type ResourcePermission, type User } from '@/lib/data';

type PermissionMap = Record<
  string,
  {
    id: string;
    create: boolean;
    read: boolean;
    update: boolean;
    delete: boolean;
    /**
     * D-COFFEE-ADMIN: a fifth, non-CRUD flag the API sends in the same
     * `resourceScope` shape (`execute.isChecked`) for resources that define
     * an Execute permission — today only `coffee-console`, whose holders may
     * flip the ordering Open/Closed switch. Additive: every node without one
     * resolves to `false`, so no existing consumer changes behaviour.
     */
    execute: boolean;
  }
>;

/**
 * ---------------------------------------------
 * Build flat web permission map
 * ---------------------------------------------
 */
export const buildWebPermissionMap = (
  webPermissions: ResourcePermission[],
): PermissionMap => {
  const map: PermissionMap = {};

  const traverse = (nodes: ResourcePermission[]) => {
    nodes.forEach((node) => {
      if (node.resourcePath) {
        map[node.resourcePath] = {
          id: node.id,
          create: !!node.resourceScope?.create?.isChecked,
          read: !!node.resourceScope?.read?.isChecked,
          update: !!node.resourceScope?.update?.isChecked,
          delete: !!node.resourceScope?.delete?.isChecked,
          execute: !!node.resourceScope?.execute?.isChecked,
        };
      }

      if (node.children?.length) {
        traverse(node.children);
      }
    });
  };

  traverse(webPermissions);

  return map;
};

/**
 * ---------------------------------------------
 * Extract base resource from route path
 * e.g.
 * /blogs -> blogs
 * /blogs/123 -> blogs
 * /claims/edit -> claims
 * ---------------------------------------------
 */
export const getResourceFromPath = (pathname: string): string => {
  return pathname.split('/').filter(Boolean)[0] || '';
};

/**
 * ---------------------------------------------
 * Get page permissions (utility function)
 * ---------------------------------------------
 */
export const getPagePermissions = (user: User, resource: string) => {
  if (!user?.permissions) return null;

  const webPermissions = user?.permissions?.web ?? [];
  const permissionMap = buildWebPermissionMap(webPermissions);
  const permission = permissionMap[resource];

  if (!permission) {
    return {
      id: '',
      create: false,
      read: false,
      update: false,
      delete: false,
      execute: false,
    };
  }

  return {
    ...permission,
  };
};
